import React from 'react';
import { FormattedMessage } from 'react-intl';
import { passwordValidator as validatePasswordErrorMessage, emailValidator } from './Validators';
import isBooleanString from './BooleanStringUtility';
import messages from '../Containers/ManualSearchV2/ManualSearchMessages';



function validateZipCode(
  searchZipCode,
  clearZipValidationError,
  zipCodeOptions,
  countryCode,
  zipCode,
  isZipCodeInvalid
) {
  if (isZipCodeInvalid && (!zipCode || zipCodeOptions.find(zipcodeOption => zipcodeOption === zipCode)))
    clearZipValidationError();

  if (zipCode && zipCode.trim().length && !zipCodeOptions.find(zipcodeOption => zipcodeOption === zipCode))
    searchZipCode({
      postalCode: zipCode,
      countryCode,
    });
}

function validateEmptyField(value, callback, errorMessage) {
  const fieldValue = value && value.trim();
  if (fieldValue) {
    callback();
  } else {
    callback(errorMessage);
  }
}

function validateStreetAddress(rule, value, callback) {
  if (value && value.length > 60) {
    callback(<FormattedMessage {...messages.addressShouldNotExceed60CharactersLabel} />);
  }
  if (!value || !value.trim().length) {
    callback(<FormattedMessage {...messages.pleaseEnterStreetAddressLabel} />);
  }
  callback();
}

function validatePhone(rule, value, callback) {
  const regex = /^\+(?:[0-9] ?){6,14}[0-9]$/;
  const phone = value;
  if (!phone || !phone.trim().length || regex.test(phone)) {
    callback();
    // Valid international phone number
  } else {
    callback(<FormattedMessage {...messages.invalidNumberLabel} />);
  }
}

function validateEmail(rule, value, callback) {
  if (!value || !value.trim().length || emailValidator(value)) {
    callback();
  } else {
    callback(<FormattedMessage {...messages.pleaseInputValidEmailLabel} />);
  }
}

function validateMinExperience(form, value, callback, isMinExpRequired) {
  const maxExp = form.getFieldValue('MaxExperience');
  if (value && !Number.isInteger(value)) callback(<FormattedMessage {...messages.expCantBeInDecimalsLabel} />);
  if (maxExp !== undefined && maxExp !== '' && maxExp !== null) {
    if (isMinExpRequired && (value === undefined || value === '' || value === null)) {
      callback(<FormattedMessage {...messages.minExpIsRequiredLabel} />);
    } else if (value > 50) {
      callback(<FormattedMessage {...messages.minExpCantBeMoreThan50YearsLabel} />);
    } else if (value > maxExp) {
      callback(<FormattedMessage {...messages.minExpCantBeGreaterThanMaxExpLabel} />);
    } else {
      callback();
    }
  } else if (value > 50) {
    callback(<FormattedMessage {...messages.minExpCantBeMoreThan50YearsLabel} />);
  } else {
    callback();
  }
  form.validateFields(['MaxExperience']);
}

function validateMaxExperience(form, value, callback, isMaxExpRequired) {
  const minExp = form.getFieldValue('MinExperience');
  if (value && !Number.isInteger(value)) callback(<FormattedMessage {...messages.expCantBeInDecimalsLabel} />);
  if (minExp !== undefined && minExp !== '' && value !== undefined && value !== '' && value !== null) {
    if (value > 50) {
      callback(<FormattedMessage {...messages.maxExpCantBeMoreThan50YearsLabel} />);
    } else if (value < minExp) {
      callback(<FormattedMessage {...messages.maxExpCantBeLessThanMinExpLabel} />);
    } else {
      callback();
    }
  } else if (
    minExp !== undefined &&
    minExp !== '' &&
    minExp !== null &&
    (value === '' || value === null || value === undefined) &&
    isMaxExpRequired
  ) {
    callback(<FormattedMessage {...messages.maxExpIsRequiredLabel} />);
  } else if (value !== undefined && value > 50) {
    callback(<FormattedMessage {...messages.maxExpCantBeMoreThan50YearsLabel} />);
  } else {
    callback();
  }
  form.validateFields(['MinExperience']);
}

function validatePassword(rule, value, callback) {
  if (!value) {
    callback(<FormattedMessage {...messages.passwordCannotBeBlankLabel} />);
  }
  const callbackMessage = validatePasswordErrorMessage(value);
  if (callbackMessage !== '') {
    callback(callbackMessage);
  } else callback();
}

const validateBooleanString = (rule, booleanString, callback, form) => {
  const modifiedBooleanString = booleanString
    ? booleanString
        .replace(
          /[\u0022|\u02BA|\u02DD|\u02EE|\u02F6|\u05F2|\u05F4|\u1CD3|\u201C|\u201D|\u201F|\u2033|\u2036|\u3003|\uFF02]/g,
          '\u0022'
        )
        .replace(/\n+/g, ' ')
        .replace(/''/g, '"') // replacing two single quotes with one double quote
    : '';
  if (!booleanString) callback();
  else if (isBooleanString(modifiedBooleanString)) {
    form.setFieldsValue({
      [rule.field]: modifiedBooleanString,
    });
    callback();
  } else {
    callback([<FormattedMessage {...messages.pleaseGiveInputInThisFormatLabel} />, <br />, <FormattedMessage {...messages.keyword1Keyword2ORKeyword3ANDKeyword4Label} />]);
  }
};

const validateDistance = (rule, value, callback, isDisabled, isEmptyDistanceAllowed, shouldHideMilesValidation) => {
  if (isDisabled) {
    callback();
    return true;
  }
  if (!value && !shouldHideMilesValidation) {
    callback(<FormattedMessage {...messages.pleasInputMilesLabel} />);
    return false;
  }
  if (value && parseInt(value, 10) !== value) {
    callback(
      <FormattedMessage {...messages.thisEntryCanOnlyContainNumbersWithoutDecimalsLabel} />
    );
    return false;
  }
  if (parseInt(value, 10) > 999) {
    callback(<FormattedMessage {...messages.mileCannotBeGreaterThan999PleaseOptForRemoteJobLabel} />);
    return false;
  }
  if ((parseInt(value, 10) >= 5 && parseInt(value, 10) <= 999) || (isEmptyDistanceAllowed && value !== 0 && !value)) {
    callback();
    return true;
  }
  if (shouldHideMilesValidation) {
    callback();
    return true;
  }
  callback(<FormattedMessage {...messages.mileMustBeGreaterThan5Label} />);
  return false;
};

export const validatePhoneIgnoringHyphensAndBraces = (rule, value, callback) => {
  const regex = /^\+(?:[0-9-()[\]\s] ?){6,14}[0-9)\]]$/;
  const phone = value;
  const isValidPhone = !phone?.trim().length || regex.test(phone.trim());
  if (isValidPhone) {
    callback();
  } else {
    callback(<FormattedMessage {...messages.invalidNumberPleaseStartWithCountryCode} />);
  }
};

export const validateTagInput = (rule, value, callback, setErrorVisiblity) => {
  const isValidTag = !value?.trim().length || value.trim()?.length <= 50;
  if (isValidTag) {
    callback();
    setErrorVisiblity(false);
  } else {
    setErrorVisiblity(true);
    callback(<FormattedMessage {...messages.aTagCannotBeMoreThan50CharactersLabel} />);
  }
};
function validateCandidateTransfer(
  value,
  callback,
  candidateIds,
  isEnableCustomSelect,
  customSelectNumber,
  invalidCandidateCallbackMessage
) {
  if (!candidateIds?.length && !(isEnableCustomSelect && customSelectNumber)) {
    callback(invalidCandidateCallbackMessage);
  }
  callback();
}

export {
  validateZipCode,
  validateEmptyField,
  validateStreetAddress,
  validatePhone,
  validateEmail,
  validateMinExperience,
  validateMaxExperience,
  validatePassword,
  validateBooleanString,
  validateDistance,
  validateCandidateTransfer,
};
